import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';
import { sanitizeGame } from './game.slice';

export const fetchHomeGames = createAsyncThunk(
  'home/fetchHomeGames',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get('/games/home');
      if (res.data.success) {
        // Format games to ensure virtual id expectations are met and only required fields are kept
        const formatGames = (games) => games.map(g => {
          const sanitized = sanitizeGame({ ...g, id: g._id || g.id });
          return {
            id: sanitized.id,
            _id: sanitized._id,
            title: sanitized.title,
            cover_image: sanitized.cover_image,
            image: sanitized.image,
            category: sanitized.category,
            rating: sanitized.rating,
            reviewCount: sanitized.reviewCount,
            description: sanitized.description,
            tagline: sanitized.tagline,
            price: sanitized.price,
            originalPrice: sanitized.originalPrice,
            discount: sanitized.discount,
            isVRReady: sanitized.isVRReady,
            tags: sanitized.tags,
            createdAt: sanitized.createdAt,
            featured: sanitized.featured,
            platforms: sanitized.platforms
          };
        });
        
        return {
          trending: formatGames(res.data.data.trending),
          newRelease: formatGames(res.data.data.newRelease),
          topSelling: formatGames(res.data.data.topSelling),
          mostPlayed: formatGames(res.data.data.mostPlayed),
          upcoming: formatGames(res.data.data.upcoming),
        };
      }
      return rejectWithValue('Failed to fetch home games');
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { home } = getState();
      if (home.loading) return false;
      if (home.trending && home.trending.length > 0) return false;
      return true;
    }
  }
);

export const fetchHomeGamesByType = createAsyncThunk(
  'home/fetchHomeGamesByType',
  async (type, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/games/home?type=${type}`);
      if (res.data.success) {
        const formatGames = (games) => games.map(g => {
          const sanitized = sanitizeGame({ ...g, id: g._id || g.id });
          return {
            id: sanitized.id,
            _id: sanitized._id,
            title: sanitized.title,
            cover_image: sanitized.cover_image,
            image: sanitized.image,
            category: sanitized.category,
            rating: sanitized.rating,
            reviewCount: sanitized.reviewCount,
            description: sanitized.description,
            tagline: sanitized.tagline,
            price: sanitized.price,
            originalPrice: sanitized.originalPrice,
            discount: sanitized.discount,
            isVRReady: sanitized.isVRReady,
            tags: sanitized.tags,
            createdAt: sanitized.createdAt,
            featured: sanitized.featured,
            platforms: sanitized.platforms
          };
        });
        
        return {
          type,
          games: formatGames(res.data.data[type] || [])
        };
      }
      return rejectWithValue(`Failed to fetch ${type} games`);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
  {
    condition: (type, { getState }) => {
      const { home } = getState();
      if (home[type] && home[type].length > 0) return false;
      return true;
    }
  }
);

const initialState = {
  trending: [],
  newRelease: [],
  topSelling: [],
  mostPlayed: [],
  upcoming: [],
  loading: false,
  error: null,
};

const homeSlice = createSlice({
  name: 'home',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHomeGames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomeGames.fulfilled, (state, action) => {
        state.trending = action.payload.trending;
        state.newRelease = action.payload.newRelease;
        state.topSelling = action.payload.topSelling;
        state.mostPlayed = action.payload.mostPlayed;
        state.upcoming = action.payload.upcoming;
        state.loading = false;
      })
      .addCase(fetchHomeGames.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchHomeGamesByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomeGamesByType.fulfilled, (state, action) => {
        const { type, games } = action.payload;
        state[type] = games;
        state.loading = false;
      })
      .addCase(fetchHomeGamesByType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export default homeSlice.reducer;
